import { Head, Link, useForm } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Edit } from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { Breadcrumbs } from "@/components/breadcrumbs"
import { goalsPath, goalPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Goals",
    href: goalsPath(),
  },
  {
    title: "Edit Goal",
    href: "/goals/edit",
  },
]

interface GoalFormData {
  id: number
  target_amount: number
  current_amount: number
  goal_type: string
  target_date: string
}

interface GoalsEditProps {
  goal: GoalFormData
  goal_types: Record<string, string>
  errors?: string[]
}

export default function GoalsEdit({
  goal,
  goal_types,
  errors = [],
}: GoalsEditProps) {
  const { data, setData, patch, processing } = useForm<GoalFormData>({
    id: goal.id,
    target_amount: goal.target_amount,
    current_amount: goal.current_amount,
    goal_type: goal.goal_type,
    target_date: goal.target_date,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(goalPath(goal.id))
  }

  return (
    <AppLayout>
      <Head title="Edit Goal" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={goalsPath()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Goals
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Edit Goal</h1>
            <p className="text-muted-foreground">
              Update your financial goal details
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Goal Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Target Amount */}
                <div className="space-y-2">
                  <Label htmlFor="target_amount">Target Amount *</Label>
                  <div className="relative">
                    <div className="text-muted-foreground absolute top-2.5 left-3">
                      $
                    </div>
                    <Input
                      id="target_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-8"
                      value={data.target_amount}
                      onChange={(e) =>
                        setData(
                          "target_amount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      required
                    />
                  </div>
                </div>

                {/* Current Amount */}
                <div className="space-y-2">
                  <Label htmlFor="current_amount">Current Amount</Label>
                  <div className="relative">
                    <div className="text-muted-foreground absolute top-2.5 left-3">
                      $
                    </div>
                    <Input
                      id="current_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-8"
                      value={data.current_amount}
                      onChange={(e) =>
                        setData(
                          "current_amount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                    />
                  </div>
                </div>

                {/* Goal Type */}
                <div className="space-y-2">
                  <Label htmlFor="goal_type">Goal Type *</Label>
                  <Select
                    value={data.goal_type}
                    onValueChange={(value) => setData("goal_type", value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a goal type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(goal_types).map(([key, value]) => (
                        <SelectItem key={key} value={key}>
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Target Date */}
                <div className="space-y-2">
                  <Label htmlFor="target_date">Target Date</Label>
                  <Input
                    id="target_date"
                    type="date"
                    value={data.target_date}
                    onChange={(e) => setData("target_date", e.target.value)}
                  />
                </div>

                {/* Error Messages */}
                {errors.length > 0 && (
                  <div className="bg-destructive/15 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-destructive text-sm font-medium">
                          There were errors with your submission:
                        </h3>
                        <div className="text-destructive mt-2 text-sm">
                          <ul role="list" className="list-disc space-y-1 pl-5">
                            {errors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <Button type="submit" disabled={processing}>
                    {processing ? "Updating..." : "Update Goal"}
                  </Button>
                  <Button variant="outline" asChild>
                    <Link href={goalsPath()}>Cancel</Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
